import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Typography, Box, Button, Drawer, Paper, IconButton, Divider, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import BalancePieChart from './BalancePieChart';

const OverviewSection = ({ handleNavigate }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { totalBalances, spotBalances, futuresBalances, aiBalances, coinPrices } = useSelector((state) => state.assets);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hideBalances, setHideBalances] = useState(false);
  const user = useSelector((state) => state.user.data);
  // حساب الرصيد الإجمالي لـ Spot Wallet
  const totalSpotBalanceInUSDT = useMemo(() => {
    if (!spotBalances || !Array.isArray(spotBalances) || spotBalances.length === 0) return '0.00';
    return spotBalances.reduce((total, balance) => {
      const symbol = balance.asset_id?.symbol?.toLowerCase() || '';
      const priceInUSD = balance.asset_id?.symbol === 'USDT'
        ? 1
        : coinPrices[symbol] || 0;
      const valueInUSD = (balance.balance || 0) * priceInUSD;
      return total + valueInUSD;
    }, 0).toFixed(2);
  }, [spotBalances, coinPrices]);

  // حساب الرصيد الإجمالي لـ Futures Wallet
  const totalFuturesBalanceInUSDT = useMemo(() => {
    if (!futuresBalances || !Array.isArray(futuresBalances) || futuresBalances.length === 0) return '0.00';
    return futuresBalances.reduce((total, balance) => {
      const symbol = balance.asset_id?.symbol?.toLowerCase() || '';
      const priceInUSD = balance.asset_id?.symbol === 'USDT'
        ? 1
        : coinPrices[symbol] || 0;
      const valueInUSD = (balance.balance || 0) * priceInUSD;
      return total + valueInUSD;
    }, 0).toFixed(2);
  }, [futuresBalances, coinPrices]);

  // حساب الرصيد الإجمالي لـ AI Wallet
  const totalAIBalanceInUSDT = useMemo(() => {
    if (!aiBalances || !Array.isArray(aiBalances) || aiBalances.length === 0) return '0.00';
    return aiBalances.reduce((total, balance) => {
      const symbol = balance.asset_id?.symbol?.toLowerCase() || '';
      const priceInUSD = balance.asset_id?.symbol === 'USDT'
        ? 1
        : coinPrices[symbol] || 0;
      const valueInUSD = (balance.balance || 0) * priceInUSD;
      return total + valueInUSD;
    }, 0).toFixed(2);
  }, [aiBalances, coinPrices]);

  // حساب الرصيد الإجمالي الكلي
  const totalBalanceInUSDT = useMemo(() => {
    return (
      parseFloat(totalSpotBalanceInUSDT || '0') +
      parseFloat(totalFuturesBalanceInUSDT || '0') +
      parseFloat(totalAIBalanceInUSDT || '0')
    ).toFixed(2);
  }, [totalSpotBalanceInUSDT, totalFuturesBalanceInUSDT, totalAIBalanceInUSDT]);

  const btcPrice = coinPrices['btc'] || 0;
  const totalBTC = btcPrice ? (totalBalanceInUSDT / btcPrice).toFixed(6) : '0.000000';
  const todayPNL = totalBalances.todayPNL || 0;
  const todayPNLPercentage = todayPNL > 0 ? '+0.000%' : '0.000%';

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const toggleCurrency = (e) => {
    e.stopPropagation();
    setHideBalances(false);
    setShowBTC(!showBTC);
  };

  const toggleHideBalances = (e) => {
    e.stopPropagation();
    setHideBalances(!hideBalances);
  };

  const [showBTC, setShowBTC] = useState(true);

  const maskBalance = (balance, isBTC = false) => {
    const length = balance.toString().length;
    return '*'.repeat(Math.max(length, isBTC ? 8 : 6));
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Est. Total Assets Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 'medium' }}>
          Est. Total Assets
        </Typography>
        <IconButton onClick={toggleHideBalances} sx={{ ml: 1, color: theme.palette.text.secondary }}>
          {hideBalances ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box onClick={toggleDrawer} sx={{ cursor: 'pointer' }}>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 'bold',
              transition: 'color 0.3s',
              '&:hover': { color: theme.palette.brand.main },
            }}
          >
            {hideBalances
              ? maskBalance(showBTC ? totalBTC : totalBalanceInUSDT, showBTC)
              : showBTC
              ? `${totalBTC} BTC`
              : `${totalBalanceInUSDT} USDT`}
          </Typography>
          <Typography
            variant={isMobile ? 'body2' : 'body1'}
            sx={{ color: theme.palette.text.secondary, mt: isMobile ? 0.2 : 0.5 }}
          >
            ≈{' '}
            {hideBalances
              ? maskBalance(showBTC ? totalBalanceInUSDT : totalBTC, !showBTC)
              : showBTC
              ? `${totalBalanceInUSDT} USD`
              : `${totalBTC} BTC`}
          </Typography>
        </Box>
        <IconButton onClick={toggleCurrency} sx={{ ml: 1, color: theme.palette.brand.main }}>
          <SwapVertIcon />
        </IconButton>
        <BalancePieChart
          spotBalance={parseFloat(totalSpotBalanceInUSDT)}
          futuresBalance={parseFloat(totalFuturesBalanceInUSDT)}
          aiBalance={parseFloat(totalAIBalanceInUSDT)}
        />
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: todayPNL >= 0 ? '#4caf50' : '#f44336',
          mt: 1,
          fontWeight: 'medium',
        }}
      >
        Today's PNL(USD) {hideBalances ? '****' : todayPNL >= 0 ? '+' : ''}{hideBalances ? '' : todayPNL.toFixed(3)} (
        {hideBalances ? '****' : todayPNLPercentage})
      </Typography>

      {/* Deposit, Withdraw, Transfer Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          variant="outlined"
          sx={{
            borderColor: theme.palette.brand.main,
            color: theme.palette.brand.main,
            flex: 1,
            mr: 1,
            borderRadius: 2,
            fontWeight: 'medium',
            transition: 'all 0.3s',
            '&:hover': {
              borderColor: theme.palette.brand.main,
              bgcolor: `${theme.palette.brand.main}20`,
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[2],
            },
          }}
          onClick={() => navigate('/deposit')}
        >
          Deposit
        </Button>
        <Button
          variant="outlined"
          sx={{
            borderColor: theme.palette.brand.main,
            color: theme.palette.brand.main,
            flex: 1,
            mx: 1,
            borderRadius: 2,
            fontWeight: 'medium',
            transition: 'all 0.3s',
            '&:hover': {
              borderColor: theme.palette.brand.main,
              bgcolor: `${theme.palette.brand.main}20`,
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[2],
            },
          }}
           onClick={() => (user && user.role === 'Support' ? navigate('/staff/withdraw-requests') : navigate('/withdraw'))}
        >
          Withdraw
        </Button>
        <Button
          variant="outlined"
          sx={{
            borderColor: theme.palette.brand.main,
            color: theme.palette.brand.main,
            flex: 1,
            ml: 1,
            borderRadius: 2,
            fontWeight: 'medium',
            transition: 'all 0.3s',
            '&:hover': {
              borderColor: theme.palette.brand.main,
              bgcolor: `${theme.palette.brand.main}20`,
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[2],
            },
          }}
          onClick={() => navigate('/transfer')}
        >
          Transfer
        </Button>
      </Box>

      {/* Accounts Section */}
      <Box sx={{ mt: 3 }}>
        <Paper
          elevation={4}
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: 3,
            overflow: 'hidden',
            transition: 'box-shadow 0.3s',
            '&:hover': { boxShadow: theme.shadows[8] },
          }}
        >
          {/* Spot Account */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 2,
              px: 2,
              cursor: 'pointer',
              background: `linear-gradient(90deg, ${theme.palette.background.paper} 0%, ${theme.palette.brand.main}10 100%)`,
              transition: 'all 0.3s',
              '&:hover': {
                background: `linear-gradient(90deg, ${theme.palette.background.paper} 0%, ${theme.palette.brand.main}20 100%)`,
                transform: 'translateX(4px)',
              },
            }}
            onClick={() => handleNavigate(1)}
          >
            <AccountBalanceWalletIcon sx={{ color: theme.palette.brand.main, mr: 2, fontSize: 28 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ color: theme.palette.text.primary, fontWeight: 'medium' }}>
                Spot Account
              </Typography>
              <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.9rem' }}>
                {hideBalances ? maskBalance(totalSpotBalanceInUSDT) : totalSpotBalanceInUSDT} USDT
              </Typography>
            </Box>
            <ArrowForwardIosIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
          </Box>

          <Divider sx={{ bgcolor: `${theme.palette.text.secondary}40` }} />

          {/* Futures Account */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 2,
              px: 2,
              cursor: 'pointer',
              background: `linear-gradient(90deg, ${theme.palette.background.paper} 0%, ${theme.palette.brand.main}10 100%)`,
              transition: 'all 0.3s',
              '&:hover': {
                background: `linear-gradient(90deg, ${theme.palette.background.paper} 0%, ${theme.palette.brand.main}20 100%)`,
                transform: 'translateX(4px)',
              },
            }}
            onClick={() => handleNavigate(2)}
          >
            <TrendingUpIcon sx={{ color: theme.palette.brand.main, mr: 2, fontSize: 28 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ color: theme.palette.text.primary, fontWeight: 'medium' }}>
                Futures Account
              </Typography>
              <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.9rem' }}>
                {hideBalances ? maskBalance(totalFuturesBalanceInUSDT) : totalFuturesBalanceInUSDT} USDT
              </Typography>
            </Box>
            <ArrowForwardIosIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
          </Box>

          <Divider sx={{ bgcolor: `${theme.palette.text.secondary}40` }} />

          {/* AI Account */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 2,
              px: 2,
              cursor: 'pointer',
              background: `linear-gradient(90deg, ${theme.palette.background.paper} 0%, ${theme.palette.brand.main}10 100%)`,
              transition: 'all 0.3s',
              '&:hover': {
                background: `linear-gradient(90deg, ${theme.palette.background.paper} 0%, ${theme.palette.brand.main}20 100%)`,
                transform: 'translateX(4px)',
              },
            }}
            onClick={() => handleNavigate(3)}
          >
            <AutoAwesomeIcon sx={{ color: theme.palette.brand.main, mr: 2, fontSize: 28 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ color: theme.palette.text.primary, fontWeight: 'medium' }}>
                AI Account
              </Typography>
              <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.9rem' }}>
                {hideBalances ? maskBalance(totalAIBalanceInUSDT) : totalAIBalanceInUSDT} USDT
              </Typography>
            </Box>
            <ArrowForwardIosIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
          </Box>
        </Paper>
      </Box>

      {/* Drawer لعرض تفاصيل الرصيد */}
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={toggleDrawer}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            p: 3,
          },
        }}
      >
        <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2 }}>
          Total Assets Breakdown
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ color: theme.palette.text.secondary }}>Total in BTC:</Typography>
          <Typography sx={{ color: theme.palette.text.primary }}>
            {hideBalances ? maskBalance(totalBTC, true) : totalBTC} BTC
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ color: theme.palette.text.secondary }}>Total in USDT:</Typography>
          <Typography sx={{ color: theme.palette.text.primary }}>
            {hideBalances ? maskBalance(totalBalanceInUSDT) : totalBalanceInUSDT} USDT
          </Typography>
        </Box>
        <Button
          variant="contained"
          sx={{
            bgcolor: theme.palette.brand.main,
            color: theme.palette.text.primary,
            mt: 2,
            borderRadius: 2,
            transition: 'all 0.3s',
            '&:hover': {
              bgcolor: theme.palette.brand.main,
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[4],
            },
          }}
          onClick={toggleDrawer}
        >
          Close
        </Button>
      </Drawer>
    </Box>
  );
};

export default OverviewSection;